// Morning Briefing pop-up — the day's three sentences + actions, shown once
// after login. (Phase 3.5a, user-directed design 2026-07-31.)
//
// The ordering rule is hard-coded and non-negotiable: CLOCK-IN FIRST. A
// briefing must never stand between an employee and their punch — late
// minutes cost salary. So the modal waits until today's attendance shows a
// punch-in (or attendance isn't in use), and otherwise arms itself on the
// 'euphoric:clocked-in' event that AttendanceHeaderButton fires on success.
// The late-reason flow lives inside the punch flow and is untouched.
//
// Respectful by design: at most once per browser session per day, one-tap
// dismiss, never on the Autopilot page (the card is already there), and
// "Read later" keeps it unread so the card still waits for them.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sun } from 'lucide-react';
import { attendanceAPI, intelligenceAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { BriefingActionRows } from './AskEuphoricPanel';

const EXCLUDED_ROLES = ['client', 'affiliate_partner'];

export default function MorningBriefingModal() {
  const { user } = useAuth();
  const location = useLocation();
  const [briefing, setBriefing] = useState(null);
  const [show, setShow] = useState(false);

  const guardKey = `mb_shown_${new Date().toISOString().slice(0, 10)}`;

  const attempt = useCallback(async () => {
    if (!user || EXCLUDED_ROLES.includes(user.role)) return;
    if (sessionStorage.getItem(guardKey)) return;
    // Clock-in first. Attendance errors (feature unused for this tenant or
    // role) fail open — the briefing is not hostage to attendance.
    try {
      const { data } = await attendanceAPI.getToday();
      const punchedIn = Boolean(data?.log?.punch_in_time);
      if (data && !punchedIn) return; // wait for 'euphoric:clocked-in'
    } catch { /* attendance not in use — proceed */ }
    try {
      const res = await intelligenceAPI.briefingToday();
      const b = res.data?.briefing;
      if (b && !b.read_at && b.sentences?.length > 0) {
        setBriefing(b);
        setShow(true);
        sessionStorage.setItem(guardKey, '1');
      }
    } catch { /* no briefing today — nothing to show */ }
  }, [user, guardKey]);

  useEffect(() => {
    if (!user) return;
    if (location.pathname.startsWith('/admin/autopilot')) return; // card lives there
    const t = setTimeout(attempt, 1200); // let the shell render first
    const onClockIn = () => setTimeout(attempt, 900); // after the punch toast
    window.addEventListener('euphoric:clocked-in', onClockIn);
    return () => {
      clearTimeout(t);
      window.removeEventListener('euphoric:clocked-in', onClockIn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!show || !briefing) return null;

  const dismiss = async (markRead) => {
    setShow(false);
    if (markRead) {
      try { await intelligenceAPI.briefingAck(); } catch { /* card remains */ }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={() => dismiss(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 bg-gradient-to-r from-slate-900 to-slate-800 flex items-center gap-2">
          <Sun size={14} className="text-amber-300" />
          <p className="text-[12px] uppercase tracking-widest text-slate-300">
            Your day at a glance
          </p>
        </div>

        {/* Everything between header and footer scrolls — a data-rich day can
            carry many points and the buttons must stay reachable. */}
        <div
          className="flex-1 overflow-y-auto min-h-0 [scrollbar-width:thin] [scrollbar-color:#cbd5e1_#f1f5f9] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full"
        >
        <div className="px-5 py-5 space-y-3">
          {briefing.sentences.map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium flex items-center justify-center tabular-nums">{i + 1}</span>
              <p className="text-[15px] text-slate-700 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>

        {briefing.actions?.length > 0 && (
          <div className="px-5 pb-4">
            {/* Tapping an action navigates to the exact lead/case and closes
                the pop-up WITHOUT marking read — the card still waits. */}
            <BriefingActionRows actions={briefing.actions}
                                onNavigate={() => dismiss(false)} />
          </div>
        )}
        </div>

        <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
          <button
            onClick={() => dismiss(false)}
            className="text-[13px] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Read later
          </button>
          <button
            onClick={() => dismiss(true)}
            className="text-[13px] text-white bg-slate-900 hover:bg-slate-800 px-4 py-1.5 rounded-lg transition-colors"
          >
            Got it — start my day
          </button>
        </div>
      </div>
    </div>
  );
}
